import { ResponsiveImage } from '@/components';
import { ImageModel } from '@/types';
import AI from '@/assets/ai.svg';

interface ImageContent {
  // Legacy format for existing scraped images
  title?: string;
  thumbnailUrl?: string | ImageModel;
  description?: string;
  imageUrl?: string;
  image?: ImageModel;
  // New format for saved reports
  pageId?: number | string;
  pageTitle?: string;
  directory?: string;
  readTime?: string;
  publishedDate?: string | Date;
  pageUrl?:
    | string
    | {
        text: string;
        href: string;
      };
  thumbnailImage?: ImageModel;
  // API response fields
  imageId?: string | number;
  imageCaption?: string;
  imageCopyright?: string;
  mediaId?: number;
  // Highlight content
  extract?: string;
  // AI Summary content
  response?: string;
}

interface SavedImageItemProps {
  image: {
    id: number;
    name: string;
    content: ImageContent | string;
    contentType?: string;
  };
  selected: boolean;
  onSelect: () => void;
  viewMode?: 'grid' | 'list';
}

const SavedImageItem: React.FC<SavedImageItemProps> = ({ image, selected, onSelect, viewMode = 'grid' }) => {
  if (!image) return null;

  // Parse content if it's a JSON string
  let parsedContent: ImageContent = {};
  if (typeof image.content === 'string') {
    try {
      parsedContent = JSON.parse(image.content);
    } catch {
      console.error('Failed to parse content JSON');
      parsedContent = {};
    }
  } else {
    parsedContent = image.content;
  }
  // Determine content type
  const isHighlight = image.contentType === 'application/x.stylus.article.extract+json' || !!parsedContent?.extract;
  const isAISummary = image.contentType === 'application/x.stylus.sytlus-ai-summary+json' || !!parsedContent?.response;

  // Create ImageModel for ResponsiveImage component
  const createImageModel = (src: string, alt?: string): ImageModel => ({
    imageUrl: src,
    imageAlt: alt || imageTitle || 'Saved image',
    caption: parsedContent?.imageCaption || '',
    copyrightText: parsedContent?.imageCopyright || '',
    mediaId: parsedContent?.mediaId || 0,
  });

  const itemClassName = `item ${selected ? 'sel' : 'unsel'}`;
  const imageSrc = isHighlight
    ? parsedContent?.extract
    : isAISummary
      ? null
      : parsedContent?.imageUrl ||
        parsedContent?.image?.imageUrl ||
        parsedContent?.thumbnailImage?.imageUrl ||
        (typeof parsedContent?.thumbnailUrl === 'string'
          ? parsedContent?.thumbnailUrl
          : parsedContent?.thumbnailUrl?.imageUrl);

  // Handle title formats
  const imageTitle = isHighlight
    ? parsedContent?.pageTitle
    : isAISummary
      ? 'AI Summary'
      : parsedContent?.imageCaption || parsedContent?.title;

  // Handle description
  const imageDescription = isAISummary
    ? parsedContent?.pageTitle
    : parsedContent?.pageTitle || parsedContent?.description;

  // Render content based on type
  const renderContent = (clampLines: number, containerClass: string) => {
    const clampClass = clampLines === 4 ? 'line-clamp-4' : 'line-clamp-6';
    if (isHighlight) {
      return (
        <div className={containerClass}>
          <div className={`shorttenedExtract ${clampClass} text-md overflow-hidden text-left text-black`}>
            {parsedContent?.extract || parsedContent?.imageUrl}
          </div>
        </div>
      );
    }
    if (isAISummary) {
      return (
        <div className={containerClass}>
          <div className="flex flex-col items-start gap-2">
            <AI className="h-8 w-8" />
            <div className={`${clampClass} overflow-hidden text-left text-sm text-black`}>
              {parsedContent?.pageTitle}
            </div>
          </div>
        </div>
      );
    }
    return (
      <ResponsiveImage
        image={createImageModel(imageSrc || '', imageTitle || 'Saved image')}
        widths={{ md: 300, lg: 400 }}
        imgProps={{
          className:
            viewMode === 'list'
              ? 'bottom-0 left-0 h-full !object-contain p-0'
              : 'thumbnailCntr absolute inset-0 !object-contain',
        }}
      />
    );
  };

  const contentLabel = isHighlight ? 'Highlight' : isAISummary ? 'AI Summary' : imageTitle;
  const contentDescription = isHighlight ? parsedContent?.pageTitle : isAISummary ? '' : imageDescription;

  if (viewMode === 'list') {
    return (
      <div className="flex h-[160px] items-center gap-4 border border-gray-200 bg-white">
        <div className="h-full w-[315px] cursor-pointer border-r border-gray-300 bg-gray-50 px-[21px] py-0">
          {renderContent(
            4,
            `flex h-full items-center ${isAISummary ? 'justify-start mb-20' : 'justify-center'} bg-gray-50 p-4`,
          )}
        </div>
        <div className="relative bottom-10 flex-1 cursor-pointer">
          <h4 className="font-bold text-gray-900">{contentLabel}</h4>
          <div className="mt-1 text-xs text-black">
            <div className="line-clamp-3 overflow-hidden">{contentDescription}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div draggable className="draggable-item">
      <div className={itemClassName}>
        <div className="selTick" onClick={onSelect}></div>
        <div className="cmp">
          <div
            className="cntr relative h-0 w-full cursor-pointer overflow-hidden border border-gray-300 bg-gray-50 pt-[30px]"
            style={{ paddingBottom: '73.25%' }}
          >
            {renderContent(
              6,
              `absolute inset-0 flex items-center ${isAISummary ? 'justify-start mb-10' : 'justify-center'} bg-gray-50 p-3`,
            )}
          </div>
          <div className="ifno relative h-[120px] cursor-pointer overflow-hidden border border-t-0 border-gray-300 bg-white p-3 text-sm">
            <div className="reportTitle">
              <h4 className="line-clamp-4 font-bold">{contentLabel}</h4>
              <div className="lnk mt-1 text-xs">
                <div className="line-clamp-3 overflow-hidden">{contentDescription}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavedImageItem;
